import type { FileChecklistTemplate } from "../types.js";

/**
 * Mortgage Architects — Alberta File Checklist (© 2024 MA).
 * Encoded as data for Prime Assistant file readiness checks.
 */
export const MA_AB_FILE_CHECKLIST_2024: FileChecklistTemplate = {
  id: "ma-ab-file-checklist-2024",
  version: "2024",
  title: "AB FILE CHECKLIST (Mortgage Architects)",
  copyright: "© Copyright 2024 MA Mortgage Architects all rights reserved.",
  defaultForBrokerages: ["mortgage-architects", "prime-one-mortgage"],
  sections: [
    { id: "header", title: "Deal header", sortOrder: 10 },
    { id: "mortgage_type", title: "Type of mortgage", sortOrder: 20 },
    { id: "required_documents", title: "Required documents", sortOrder: 30 },
    { id: "mpp", title: "MPP", sortOrder: 40 },
    { id: "kyc", title: "Know your client", sortOrder: 50 },
    { id: "income", title: "Income verification (as requested by lender)", sortOrder: 60 },
    { id: "down_payment", title: "Down payment verification (as requested by lender)", sortOrder: 70 },
    { id: "property", title: "Property agreements (if applicable)", sortOrder: 80 },
    { id: "appraisal", title: "Appraisal", sortOrder: 90 },
    { id: "other", title: "Other (if applicable)", sortOrder: 100 },
    { id: "backup", title: "Backup documents", sortOrder: 110 },
    { id: "fraud", title: "Fraud due diligence", sortOrder: 120 },
  ],
  mortgageTypeOptions: [
    { id: "purchase", label: "Purchase" },
    { id: "first_mortgage", label: "First mortgage" },
    { id: "second_mortgage", label: "Second mortgage" },
    { id: "refinance_eto", label: "Refinance / ETO" },
    { id: "line_of_credit", label: "Line of credit / Visa" },
    { id: "private", label: "Private" },
    { id: "commercial", label: "Commercial" },
    { id: "construction", label: "Construction" },
    { id: "reverse", label: "Reverse mortgage / CHIP" },
    { id: "renewal", label: "Renewal" },
    { id: "switch_transfer", label: "Switch / transfer" },
    { id: "first_time_home_buyer", label: "First-time home buyer" },
    { id: "cancelled_declined", label: "Cancelled / declined" },
    { id: "co_brokered", label: "Co-brokered deal" },
  ],
  items: [
    // Header
    {
      id: "header_client_names",
      sectionId: "header",
      sortOrder: 1,
      label: "Client name(s)",
      kind: "deal_field",
      dealFields: ["clientNames"],
    },
    {
      id: "header_broker_names",
      sectionId: "header",
      sortOrder: 2,
      label: "Broker / agent name(s)",
      kind: "deal_field",
      dealFields: ["brokerNames"],
    },
    {
      id: "header_deal_number",
      sectionId: "header",
      sortOrder: 3,
      label: "Deal number",
      kind: "deal_field",
      dealFields: ["dealNumber"],
    },
    {
      id: "header_closing_date",
      sectionId: "header",
      sortOrder: 4,
      label: "Closing date",
      kind: "deal_field",
      dealFields: ["closingDate"],
    },
    {
      id: "header_property_province",
      sectionId: "header",
      sortOrder: 5,
      label: "Property province",
      kind: "deal_field",
      dealFields: ["propertyProvince"],
    },
    {
      id: "header_inter_provincial",
      sectionId: "header",
      sortOrder: 6,
      label:
        "Inter-provincial deal — disclosures signed by licensed broker in property province (if applicable)",
      kind: "document",
      matchRuleId: "inter_provincial_disclosure",
      showWhen: { factTrue: "interProvincial" },
    },
    // Mortgage type flags (broker confirms selection; Velocity may hint)
    ...[
      "purchase",
      "first_mortgage",
      "second_mortgage",
      "refinance_eto",
      "line_of_credit",
      "private",
      "commercial",
      "construction",
      "reverse",
      "renewal",
      "switch_transfer",
      "first_time_home_buyer",
      "cancelled_declined",
      "co_brokered",
    ].map((id, i) => ({
      id: `mortgage_type_${id}`,
      sectionId: "mortgage_type",
      sortOrder: 10 + i,
      label: `Mortgage type: ${id.replace(/_/g, " ")}`,
      kind: "mortgage_type" as const,
      dealFields: [`mortgageTypes.${id}`],
    })),
    {
      id: "co_broker_details",
      sectionId: "mortgage_type",
      sortOrder: 30,
      label: "Co-broker name, company, and split details (notes)",
      kind: "deal_field",
      dealFields: ["coBrokerDetails"],
      showWhen: { anyMortgageType: ["co_brokered"] },
    },
    {
      id: "cancelled_declined_docs",
      sectionId: "mortgage_type",
      sortOrder: 31,
      label: "Cancelled / declined — upload consent and any client documents obtained",
      kind: "document",
      matchRuleId: "cancelled_declined_consent",
      showWhen: { anyMortgageType: ["cancelled_declined"] },
    },
    // Required documents
    {
      id: "aml_pep_declaration",
      sectionId: "required_documents",
      sortOrder: 1,
      label: "AML — signed PEP declaration for each borrower",
      kind: "document",
      matchRuleId: "pep_declaration",
      perBorrower: true,
    },
    {
      id: "aml_id_verification",
      sectionId: "required_documents",
      sortOrder: 2,
      label:
        "AML — ID verification / validation / authentication (method documented: in-person photo ID, credit bureau 3+ years, FastKey/IDV, or dual method)",
      kind: "document",
      matchRuleId: "aml_id_verification",
      perBorrower: true,
      helpText:
        "Broker must record method: (1) Gov't photo ID in-person date/time/location, (2) CB 3+ years, (3) FastKey through Velocity, or (4) dual method from reliable sources.",
    },
    {
      id: "photo_id_all_borrowers",
      sectionId: "required_documents",
      sortOrder: 3,
      label: "Photo ID for all borrowers",
      kind: "document",
      matchRuleId: "photo_id",
      perBorrower: true,
    },
    {
      id: "client_engagement_letter",
      sectionId: "required_documents",
      sortOrder: 4,
      label:
        "Client engagement letter (includes consent, KYC). Consent before ordering credit bureau.",
      kind: "document",
      matchRuleId: "client_engagement_letter",
    },
    {
      id: "credit_bureau_online_consent",
      sectionId: "required_documents",
      sortOrder: 5,
      label: "Online consent received prior to client engagement letter (credit bureau ordering)",
      kind: "document_ordering",
      matchRuleId: "online_consent_credit",
      ordering: {
        beforeItemMatchRuleId: "online_consent_credit",
        afterItemMatchRuleId: "client_engagement_letter",
        label: "Online consent should be received before the client engagement letter when that box applies.",
      },
    },
    {
      id: "credit_bureau",
      sectionId: "required_documents",
      sortOrder: 6,
      label: "Credit bureau",
      kind: "document",
      matchRuleId: "credit_bureau",
    },
    {
      id: "consent_before_credit_bureau",
      sectionId: "required_documents",
      sortOrder: 7,
      label: "Consent obtained before ordering credit bureau",
      kind: "document_ordering",
      matchRuleId: "client_engagement_letter",
      ordering: {
        beforeItemMatchRuleId: "client_engagement_letter",
        afterItemMatchRuleId: "credit_bureau",
        label: "Engagement letter / consent should precede credit bureau pull.",
      },
    },
    {
      id: "mortgage_application",
      sectionId: "required_documents",
      sortOrder: 8,
      label: "Mortgage application / mortgage summary",
      kind: "document",
      matchRuleId: "mortgage_application",
    },
    {
      id: "product_suitability",
      sectionId: "required_documents",
      sortOrder: 9,
      label:
        "Mortgage product suitability assessment completed, documented, acknowledged by client(s) (broker to initial)",
      kind: "document",
      matchRuleId: "product_suitability",
      helpText: "Includes KYC, KYP, options presented, recommendations, rationale.",
    },
    {
      id: "material_risk_form",
      sectionId: "required_documents",
      sortOrder: 10,
      label: "Material risk form completed and signed by borrower (Velocity)",
      kind: "document",
      matchRuleId: "material_risk_form",
    },
    {
      id: "lender_commitment",
      sectionId: "required_documents",
      sortOrder: 11,
      label: "Lender commitment signed by all borrowers and guarantors",
      kind: "document",
      matchRuleId: "lender_commitment",
    },
    {
      id: "written_service_agreement",
      sectionId: "required_documents",
      sortOrder: 12,
      label: "Written service agreement (WSA) — signed/dated prior to completing application",
      kind: "document",
      matchRuleId: "written_service_agreement",
    },
    {
      id: "wsa_before_application",
      sectionId: "required_documents",
      sortOrder: 13,
      label: "WSA dated before mortgage application",
      kind: "document_ordering",
      matchRuleId: "written_service_agreement",
      ordering: {
        beforeItemMatchRuleId: "written_service_agreement",
        afterItemMatchRuleId: "mortgage_application",
        label: "WSA should be signed/dated before the mortgage application.",
      },
    },
    {
      id: "initial_statement_disclosure",
      sectionId: "required_documents",
      sortOrder: 14,
      label:
        "Initial statement of disclosure (Alberta Fair Trading Act) — broker fee / private / commercial; 2 business day cooling off unless waived",
      kind: "document",
      matchRuleId: "initial_statement_disclosure",
      showWhen: { custom: "isd_required" },
    },
    // MPP
    {
      id: "mpp_application",
      sectionId: "mpp",
      sortOrder: 1,
      label: "MPP application (accepted)",
      kind: "document",
      matchRuleId: "mpp_application",
      showWhen: { factTrue: "mppAccepted" },
    },
    {
      id: "mpp_waived_documentation",
      sectionId: "mpp",
      sortOrder: 2,
      label: "MPP waived — indemnification or no-offer form in file",
      kind: "document",
      matchRuleId: "mpp_indemnification",
      showWhen: { factTrue: "mppWaived" },
      helpText: "Include MPP indemnification form or no-offer form if ineligible.",
    },
    {
      id: "mpp_indemnification_always",
      sectionId: "mpp",
      sortOrder: 3,
      label: "MPP indemnification form (included in compliance files)",
      kind: "document",
      matchRuleId: "mpp_indemnification",
    },
    {
      id: "solicitor_letter_direction",
      sectionId: "mpp",
      sortOrder: 4,
      label: "Solicitor letter of direction (if broker fee charged)",
      kind: "document",
      matchRuleId: "solicitor_letter_direction",
      showWhen: { custom: "mpp_fee_solicitor" },
    },
    {
      id: "reverse_ila",
      sectionId: "mpp",
      sortOrder: 5,
      label: "Reverse mortgage — ILA confirmation (lawyer letterhead/stamp)",
      kind: "document",
      matchRuleId: "reverse_ila",
      showWhen: { anyMortgageType: ["reverse"] },
    },
    // KYC
    {
      id: "kyc_notes_retention",
      sectionId: "kyc",
      sortOrder: 1,
      label:
        "KYC — notes/emails/texts with clients/lenders/parties retained and available to brokerage (7 years)",
      kind: "informational",
      helpText:
        "Prime cannot verify retention period; broker confirms notes are stored per brokerage policy.",
    },
    // Income — per lender request
    {
      id: "income_job_letters",
      sectionId: "income",
      sortOrder: 1,
      label: "Job letter(s)",
      kind: "lender_requested_income",
      matchRuleId: "job_letter",
      showWhen: { custom: "income_docs_requested" },
    },
    {
      id: "income_paystubs",
      sectionId: "income",
      sortOrder: 2,
      label: "Paystubs",
      kind: "lender_requested_income",
      matchRuleId: "paystub",
      showWhen: { custom: "income_docs_requested" },
    },
    {
      id: "income_t4_t1",
      sectionId: "income",
      sortOrder: 3,
      label: "T4s / T1 general",
      kind: "lender_requested_income",
      matchRuleId: "t4_t1",
      showWhen: { custom: "income_docs_requested" },
    },
    {
      id: "income_noa",
      sectionId: "income",
      sortOrder: 4,
      label: "NOA(s)",
      kind: "lender_requested_income",
      matchRuleId: "noa",
      showWhen: { custom: "income_docs_requested" },
    },
    {
      id: "income_declared",
      sectionId: "income",
      sortOrder: 5,
      label: "Declared income",
      kind: "lender_requested_income",
      matchRuleId: "declared_income",
      showWhen: { custom: "income_docs_requested" },
    },
    {
      id: "income_pension",
      sectionId: "income",
      sortOrder: 6,
      label: "Pension",
      kind: "lender_requested_income",
      matchRuleId: "pension_income",
      showWhen: { custom: "income_docs_requested" },
    },
    // Down payment
    {
      id: "dp_bank_statements",
      sectionId: "down_payment",
      sortOrder: 1,
      label: "Bank statements",
      kind: "lender_requested_down_payment",
      matchRuleId: "bank_statement",
      showWhen: { custom: "down_payment_docs_requested" },
    },
    {
      id: "dp_gift_letter",
      sectionId: "down_payment",
      sortOrder: 2,
      label: "Gift letter",
      kind: "lender_requested_down_payment",
      matchRuleId: "gift_letter",
      showWhen: { custom: "down_payment_docs_requested" },
    },
    {
      id: "dp_sale_previous",
      sectionId: "down_payment",
      sortOrder: 3,
      label: "Sale of previous house",
      kind: "lender_requested_down_payment",
      matchRuleId: "purchase_sale_agreement",
      showWhen: { custom: "down_payment_docs_requested" },
    },
    {
      id: "dp_other",
      sectionId: "down_payment",
      sortOrder: 5,
      label: "Other down payment verification",
      kind: "informational",
      showWhen: { custom: "down_payment_docs_requested" },
    },
    // Property
    {
      id: "property_purchase_sale",
      sectionId: "property",
      sortOrder: 1,
      label: "Purchase / sale agreement",
      kind: "document",
      matchRuleId: "purchase_sale_agreement",
      showWhen: { custom: "property_agreements_applicable" },
    },
    {
      id: "property_rental",
      sectionId: "property",
      sortOrder: 2,
      label: "Rental agreement",
      kind: "document",
      matchRuleId: "rental_agreement",
      showWhen: { custom: "property_agreements_applicable" },
    },
    {
      id: "property_listing",
      sectionId: "property",
      sortOrder: 3,
      label: "Listing",
      kind: "document",
      matchRuleId: "listing",
      showWhen: { custom: "property_agreements_applicable" },
    },
    // Appraisal
    {
      id: "appraisal_document",
      sectionId: "appraisal",
      sortOrder: 1,
      label: "Appraisal (borrower / lender direct / waived / insured)",
      kind: "document",
      matchRuleId: "appraisal",
      helpText: "Broker selects mode on form; document required unless waived or insured path documented.",
    },
    // Other
    {
      id: "other_divorce",
      sectionId: "other",
      sortOrder: 1,
      label: "Divorce / separation agreement (if applicable)",
      kind: "document",
      matchRuleId: "divorce_separation",
    },
    {
      id: "other_bankruptcy",
      sectionId: "other",
      sortOrder: 2,
      label: "Bankruptcy discharge (if applicable)",
      kind: "document",
      matchRuleId: "bankruptcy_discharge",
    },
    {
      id: "backup_docs_noted",
      sectionId: "backup",
      sortOrder: 1,
      label: "All other backup documents noted and filed behind this checklist",
      kind: "informational",
    },
    {
      id: "fraud_acknowledgment",
      sectionId: "fraud",
      sortOrder: 1,
      label:
        "Acknowledgment: reasonable due diligence to detect and prevent fraud (broker initials)",
      kind: "attestation",
      matchRuleId: "fraud_acknowledgment",
    },
  ],
};
